/**
 * Authentication Service
 * Wrapper around Firebase Auth providing application-specific auth methods
 * Handles sign up, sign in, sign out, email verification, and password reset
 */

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';
import { firebaseAuth } from '@/config/firebase';
import { apiPost } from './apiClient';

/**
 * Subscribe to auth state changes
 * @param {Function} callback - Called with user object or null
 * @returns {Function} Unsubscribe function
 */
function onAuthChange(callback) {
  return onAuthStateChanged(firebaseAuth, callback);
}

/**
 * Sign up a new user with email and password
 * Automatically sends email verification via Brevo after account creation
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {string} displayName - Optional display name
 * @returns {Promise<import('firebase/auth').User>} Firebase user object
 * @throws {Error} If registration fails
 */
async function signUp(email, password, displayName = null) {
  const credential = await createUserWithEmailAndPassword(firebaseAuth, email, password);

  // Update profile with display name if provided
  if (displayName) {
    await updateProfile(credential.user, { displayName });
  }

  // Send beautiful email verification via Brevo
  try {
    await apiPost('/auth/send-verification');
  } catch (err) {
    console.warn('Failed to send verification email:', err);
  }

  return credential.user;
}

/**
 * Sign in existing user with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<import('firebase/auth').UserCredential>} Firebase user credential
 * @throws {Error} If sign in fails
 */
function signIn(email, password) {
  return signInWithEmailAndPassword(firebaseAuth, email, password);
}

/**
 * Sign out current user
 * @returns {Promise<void>}
 */
function signOut() {
  return fbSignOut(firebaseAuth);
}

/**
 * Send email verification to current user via Brevo
 * @returns {Promise<void>}
 * @throws {Error} If no user is signed in
 */
async function sendEmailVerificationToCurrentUser() {
  if (!firebaseAuth.currentUser) {
    throw new Error('No user is currently signed in');
  }
  return apiPost('/auth/send-verification');
}

/**
 * Send password reset email via Brevo
 * @param {string} email - User email to send reset link to
 * @returns {Promise<void>}
 */
function sendPasswordReset(email) {
  return apiPost('/auth/send-password-reset', { email });
}

/**
 * Verify email using token from Brevo email
 * @param {string} token - Verification token from email link
 * @returns {Promise<object>}
 */
function verifyEmail(token) {
  return apiPost('/auth/verify-email', { token });
}

/**
 * Verify password reset token
 * @param {string} token - Reset token from email link
 * @returns {Promise<object>}
 */
function verifyResetToken(token) {
  return apiPost('/auth/verify-reset-token', { token });
}

/**
 * Reset password using token
 * @param {string} token - Reset token from email link
 * @param {string} password - New password
 * @returns {Promise<object>}
 */
function resetPassword(token, password) {
  return apiPost('/auth/reset-password', { token, password });
}

/**
 * Reload current user to refresh email verification status
 * @returns {Promise<void>}
 * @throws {Error} If no user is signed in
 */
async function refreshUser() {
  if (!firebaseAuth.currentUser) {
    throw new Error('No user is currently signed in');
  }
  await firebaseAuth.currentUser.reload();
  return firebaseAuth.currentUser;
}

/**
 * Get current user's ID token
 * @param {boolean} forceRefresh - Force token refresh
 * @returns {Promise<string|null>} ID token or null if not authenticated
 */
async function getIdToken(forceRefresh = false) {
  if (!firebaseAuth.currentUser) {
    return null;
  }

  try {
    return await firebaseAuth.currentUser.getIdToken(forceRefresh);
  } catch (error) {
    console.warn('Unable to get ID token:', error);
    return null;
  }
}

export const authService = {
  onAuthStateChanged: onAuthChange,
  signUp,
  signIn,
  signOut,
  sendEmailVerification: sendEmailVerificationToCurrentUser,
  sendPasswordReset,
  verifyEmail,
  verifyResetToken,
  resetPassword,
  refreshUser,
  getIdToken
};

// Default export for convenience
export default authService;
